import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  EnvironmentOutlined,
  FileSearchOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  APP_NAME,
  VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY,
} from "../../helpers/constants";
import { device } from "../../lib/breakpoints";

const PANEL = "#0f172a";
const CARD = "#1e293b";
const DEEP = "#020617";
const BORDER = "#334155";
const TEXT = "#f1f5f9";
const MUTED = "#94a3b8";
const ORANGE = "#ff5722";
const ORANGE_HOVER = "#fb923c";

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(
      ellipse 120% 80% at 50% -20%,
      rgba(255, 87, 34, 0.08) 0%,
      transparent 55%
    ),
    linear-gradient(165deg, ${DEEP} 0%, ${PANEL} 42%, #111827 100%);
  padding: clamp(0.75rem, 2vw, 1.25rem) 1.25rem 5rem;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Shell = styled.div`
  max-width: 680px;
  margin: 0 auto;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const BrandName = styled.div`
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${TEXT};
`;

const BrandTag = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${ORANGE};
`;

const HeroSection = styled.div`
  text-align: center;
  margin-top: clamp(1rem, 2.5vw, 1.75rem);
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-top: 2.25rem;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.85rem;
  background: rgba(255, 87, 34, 0.12);
  border: 1px solid rgba(255, 87, 34, 0.3);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ORANGE_HOVER};
  margin-bottom: 1rem;
`;

const Headline = styled.h1`
  margin: 0 0 0.75rem;
  font-weight: 800;
  color: ${TEXT};
  line-height: 1.2;
  font-size: clamp(1.5rem, 4.5vw, 2rem);
  letter-spacing: -0.03em;
  text-wrap: balance;

  span {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    color: inherit;
    font-family: inherit;
  }
`;

const Subhead = styled.p`
  margin: 0 auto;
  max-width: 520px;
  font-size: 1rem;
  line-height: 1.6;
  color: ${MUTED};

  strong {
    color: ${TEXT};
    font-weight: 600;
  }
`;

const PriceCard = styled.div`
  background: ${CARD};
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 1.75rem 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 1.25rem;
    right: 1.25rem;
    height: 3px;
    border-radius: 0 0 4px 4px;
    background: linear-gradient(
      90deg,
      ${ORANGE},
      #fb923c,
      rgba(255, 87, 34, 0.35)
    );
  }
`;

const PriceBlock = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;
`;

const PriceRow = styled.div`
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`;

const PriceAmount = styled.div`
  font-size: clamp(2rem, 6vw, 2.75rem);
  font-weight: 800;
  color: ${TEXT};
  letter-spacing: -0.04em;
  line-height: 1;
`;

const PricePer = styled.div`
  font-size: 0.9rem;
  color: ${MUTED};
  padding-bottom: 0.3rem;
`;

const PriceNote = styled.div`
  font-size: 0.8125rem;
  color: ${MUTED};
`;

const BenefitGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
  width: 100%;
`;

const BenefitRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
`;

const BenefitIcon = styled.div`
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 6px;
  background: rgba(255, 87, 34, 0.12);
  border: 1px solid rgba(255, 87, 34, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ORANGE};
  font-size: 0.875rem;
  margin-top: 0.05rem;
`;

const BenefitText = styled.div`
  flex: 1;
  text-align: left;
`;

const BenefitTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${TEXT};
  margin-bottom: 0.15rem;
  text-align: left;
`;

const BenefitDesc = styled.div`
  font-size: 0.8125rem;
  color: ${MUTED};
  line-height: 1.45;
  text-align: left;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${BORDER};
  margin: 1.25rem 0 0;
`;

const CardNote = styled.p`
  text-align: center;
  font-size: 0.8rem;
  color: ${MUTED};
  margin: 1rem 0 0;
  line-height: 1.5;

  a {
    color: ${ORANGE_HOVER};
    font-weight: 500;
    text-decoration: none;
  }
`;

const ComparisonTable = styled.div`
  background: ${CARD};
  border: 1px solid ${BORDER};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const CompRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding: 0.7rem 1rem;
  gap: 0.75rem;
  border-bottom: 1px solid ${BORDER};

  &:last-child {
    border-bottom: none;
  }

  &.header {
    background: rgba(15, 23, 42, 0.6);
    padding: 0.5rem 1rem;
  }
`;

const CompLabel = styled.div`
  font-size: 0.875rem;
  color: ${MUTED};

  .header & {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
  }
`;

const CompCell = styled.div`
  text-align: center;
  min-width: 72px;
  font-size: 0.8125rem;

  .header & {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ $priority }) => ($priority ? ORANGE : "#64748b")};
  }
`;

const CheckMark = styled.span`
  color: #34d399;
  font-weight: 700;
`;

const CrossMark = styled.span`
  color: #475569;
`;

const PriorityMark = styled.span`
  color: ${ORANGE};
  font-weight: 600;
`;

const CtaButton = styled(Link)`
  display: block;
  width: 100%;
  text-align: center;
  padding: 0.9rem 1.5rem;
  background: ${ORANGE};
  color: #fff;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(255, 87, 34, 0.35);
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: ${ORANGE_HOVER};
    color: #fff;
    box-shadow: 0 6px 20px rgba(255, 87, 34, 0.42);
  }
`;

const BottomCtaSection = styled.div`
  margin-top: 2.25rem;
`;

const CtaNote = styled.p`
  text-align: center;
  font-size: 0.8rem;
  color: ${MUTED};
  margin: 0.6rem 0 0;
`;

const SecondaryLink = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${MUTED};

  a {
    color: ${ORANGE_HOVER};
    font-weight: 500;
    text-decoration: none;

    &:hover {
      color: #fdba74;
    }
  }
`;

const FooterNote = styled.p`
  text-align: center;
  margin: 1.5rem 0 0;
  font-size: 0.8rem;
  color: #475569;

  a {
    color: ${ORANGE_HOVER};
    font-weight: 500;
    text-decoration: none;
  }
`;

const BENEFITS = [
  {
    Icon: RiseOutlined,
    title: "First on bid lists",
    desc: "Rank above free members when owners invite bids in your ZIP.",
  },
  {
    Icon: SafetyCertificateOutlined,
    title: "Vetted badge",
    desc: "Show owners the Clumse-vetted badge on your profile.",
  },
  {
    Icon: FileSearchOutlined,
    title: "Scoped jobs, not cold leads",
    desc: "Every invite includes scope, property, ZIP, and timeline.",
  },
  {
    Icon: EnvironmentOutlined,
    title: "Your trades and ZIPs only",
    desc: "Only jobs that match your trades and service area.",
  },
  {
    Icon: StarOutlined,
    title: "No per-lead fees",
    desc: "One flat monthly fee with unlimited matched invites.",
  },
];

const COMPARISON = [
  { label: "Bid invitations by email / SMS", free: true, priority: true },
  { label: "Matched to your trades and ZIPs", free: true, priority: true },
  { label: "Job scope included with each invite", free: true, priority: true },
  {
    label: "Appear above free members on bid lists",
    free: false,
    priority: true,
  },
  { label: "Vetted badge visible to owners", free: false, priority: true },
  { label: "Priority review for new job matches", free: false, priority: true },
  { label: "Monthly fee", free: "Free", priority: "$99/mo" },
];

const VendorPriorityPage = () => (
  <Page>
    <Helmet>
      <title>Priority placement for vendors | {APP_NAME}</title>
      <meta
        name="description"
        content="Appear first on Clumse owner bid lists in your area. Vetted priority placement is $99/month after approval."
      />
    </Helmet>
    <Shell>
      <HeroSection>
        <Badge>
          <StarOutlined aria-hidden /> Priority placement
        </Badge>
        <Headline>
          Get to the top of every bid list&nbsp;in&nbsp;your&nbsp;area
        </Headline>
        <Subhead>
          Free network members get matched to jobs.{" "}
          <strong>Priority members get seen first</strong>, show a vetted badge,
          and get reviewed ahead of free members on every invite.
        </Subhead>
      </HeroSection>

      <PriceCard>
        <PriceBlock>
          <PriceRow>
            <PriceAmount>
              {VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY.replace("/month", "")}
            </PriceAmount>
            <PricePer>/ month</PricePer>
          </PriceRow>
          <PriceNote>
            After approval. Cancel anytime. Nothing is charged on this page.
          </PriceNote>
        </PriceBlock>
        <BenefitGrid>
          {BENEFITS.map(({ Icon, title, desc }) => (
            <BenefitRow key={title}>
              <BenefitIcon aria-hidden>
                <Icon />
              </BenefitIcon>
              <BenefitText>
                <BenefitTitle>{title}</BenefitTitle>
                <BenefitDesc>{desc}</BenefitDesc>
              </BenefitText>
            </BenefitRow>
          ))}
        </BenefitGrid>
        <Divider />
        <CardNote>
          Already on the free network? Email{" "}
          <a href="mailto:vendors@clumse.com">vendors@clumse.com</a> and
          we&apos;ll send vetting details.
        </CardNote>
      </PriceCard>

      <ComparisonTable aria-label="Free network vs priority placement comparison">
        <CompRow className="header">
          <CompLabel className="header" />
          <CompCell className="header">Free</CompCell>
          <CompCell className="header" $priority>
            Priority
          </CompCell>
        </CompRow>
        {COMPARISON.map(({ label, free, priority }) => (
          <CompRow key={label}>
            <CompLabel>{label}</CompLabel>
            <CompCell>
              {free === true ? (
                <CheckMark>✓</CheckMark>
              ) : free === false ? (
                <CrossMark>-</CrossMark>
              ) : (
                <span style={{ color: MUTED }}>{free}</span>
              )}
            </CompCell>
            <CompCell>
              {priority === true ? (
                <CheckMark>✓</CheckMark>
              ) : priority === false ? (
                <CrossMark>-</CrossMark>
              ) : (
                <PriorityMark>{priority}</PriorityMark>
              )}
            </CompCell>
          </CompRow>
        ))}
      </ComparisonTable>

      <BottomCtaSection>
        <CtaButton to="/vendors/apply?program=priority_placement">
          Apply for priority placement
        </CtaButton>
        <CtaNote>Nothing is charged until approval. No per-lead fees.</CtaNote>
      </BottomCtaSection>

      <SecondaryLink>
        Just want the free network?{" "}
        <Link to="/vendors/apply">Apply at no cost</Link>
      </SecondaryLink>

      <FooterNote>
        Questions? Email{" "}
        <a href="mailto:vendors@clumse.com">vendors@clumse.com</a>
      </FooterNote>
    </Shell>
  </Page>
);

export default VendorPriorityPage;
